import { z } from "zod";
import { bottleFormatSchema } from "./lot.js";
import { eventNoteSchema } from "./bottle.js";
/**
 * Walking a shelf, and what the walk concluded.
 *
 * The record drifts from the cabinet — bottles get moved without being
 * recorded, carried to dinners and given away in moments nobody logged — and
 * `project-scope.md` puts stocktaking in the application precisely to admit
 * that rather than to pretend otherwise. An inventory that claims to be exact
 * stops being trusted, and an inventory nobody trusts ends up back in a
 * spreadsheet.
 *
 * Three things can be true of a bottle the record places on this shelf, and
 * the contract carries exactly those three:
 *
 * - it is standing there, and is **confirmed**;
 * - it is standing there and the record had it somewhere else, which is
 *   **found** — the move nobody wrote down, written now;
 * - it is not there and has been given up on, which is **gone**.
 *
 * The fourth case is the common one and has no field: a bottle that is simply
 * not ticked. Not confirming is a result — decided 2026-09-15 — and it is
 * recorded by the `lastConfirmedAt` that stayed old, which is what that field
 * was put in the schema in release 1 to do.
 */
/** A calendar date with no time in it, as `YYYY-MM-DD`. */
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * How a bottle that is not on the shelf is given up on.
 *
 * The four departures plus the consumption, because *drunk and never written
 * down* is the likeliest thing that happened to a bottle that is not there —
 * and because `project-scope.md` names drunk first among the resolutions of a
 * missing bottle. It is not `BottleDeparture` with a fifth value bolted on:
 * that enum is what the departure route accepts, and a bottle cannot be
 * *consumed* through it because consuming carries a tasting note and this does
 * not. Nobody writes up a wine standing in front of a cabinet counting it.
 */
export const stocktakeOutcomeSchema = z.enum([
    "Consumed",
    "Gifted",
    "Sold",
    "Lost",
    "Broken",
]);
export const goneBottleSchema = z.object({
    bottleId: z.string().min(1),
    outcome: stocktakeOutcomeSchema,
    note: eventNoteSchema,
});
/**
 * One walk, sent once, at the end of it.
 *
 * One request rather than a write per row, because a walk is one session in
 * front of one shelf and the record of it should be one row or none. It also
 * means the walk survives a cellar with no signal the way the rest of the
 * application does: what was ticked is held in the browser until there is
 * something to send it to, and a refused save keeps it rather than losing
 * twenty bottles' worth of looking.
 *
 * `walkedOn` is when somebody looked, not when what they found had happened.
 * A bottle missing in September may have been drunk in March, and the walk has
 * no way of knowing; the date it writes is the honest one, and correcting a
 * departure's date afterwards is a typing correction that already exists.
 *
 * The ceilings are the move route's 240: a shelf that holds more than that is
 * a room, not a shelf.
 */
export const recordStocktakeRequestSchema = z.object({
    shelfId: z.string().min(1),
    walkedOn: dateOnly,
    /** Bottles the record places here and that are standing here. */
    confirmed: z.array(z.string().min(1)).max(240).default([]),
    /**
     * Bottles standing here that the record places somewhere else. They are
     * moved onto this shelf, and the move is the confirmation: nobody moves a
     * bottle they did not have in their hand, so writing `Confirmed` beside
     * `Moved` would be two events for one look.
     */
    found: z.array(z.string().min(1)).max(240).default([]),
    /** Bottles the record places here that have been given up on. */
    gone: z.array(goneBottleSchema).max(240).default([]),
});
/**
 * What the walk wrote, counted — and what it left open.
 *
 * `unconfirmed` is the number that matters and the reason this response is not
 * a bare 204: it is how many bottles the record still places on this shelf
 * that nobody has seen. The screen says it out loud at the end of the walk,
 * because a walk that quietly left six bottles in doubt would be the drift
 * this feature exists to surface, reintroduced by the feature itself.
 */
export const stocktakeResponseSchema = z.object({
    stocktakeId: z.string(),
    walkedOn: z.string(),
    confirmed: z.number().int(),
    moved: z.number().int(),
    gone: z.number().int(),
    unconfirmed: z.number().int(),
});
/**
 * One bottle as the walk meets it: an object to be found or not found.
 *
 * Bottles of one wine on one shelf are physically indistinguishable, so the
 * screen asks for a count and resolves it to ids — the same thing the move
 * route says about itself. The ids still travel, because what goes into the
 * history has to be the actual objects or the event log stops being able to
 * answer where a given bottle has been.
 */
export const shelfBottleSchema = z.object({
    id: z.string(),
    format: bottleFormatSchema,
    /** The day it was last seen, by a walk. Null for a bottle nobody has ever
     *  confirmed, which is every bottle in this cellar until the first walk. */
    lastConfirmedAt: z.string().nullable(),
});
/** The wines standing on the shelf, in the order the labels are numbered. */
export const shelfWineSchema = z.object({
    wineId: z.string(),
    number: z.number().int(),
    name: z.string(),
    producer: z.string(),
    vintage: z.number().int().nullable(),
    isNonVintage: z.boolean(),
    bottles: z.array(shelfBottleSchema),
});
/**
 * What the record says is on this shelf, which is the thing the walk is
 * checking against.
 *
 * Read fresh at the start of a walk rather than reused from the wine list: the
 * list carries where a wine's bottles stand, but not which bottles those are,
 * and a walk writes events against objects.
 */
export const shelfContentsResponseSchema = z.object({
    shelfId: z.string(),
    shelfName: z.string(),
    storageName: z.string(),
    capacity: z.number().int().nullable(),
    /** The last walk past this shelf, so the screen can say when it was. */
    lastWalkedAt: z.string().nullable(),
    wines: z.array(shelfWineSchema),
});
//# sourceMappingURL=stocktake.js.map